/**
 * RSVPs API Client
 * Frontend API calls for managing RSVPs
 */

import { apiCall } from '../api';
import type {
  RSVP,
  RSVPResponse,
  RSVPsResponse,
  RSVPsWithFeedbackResponse,
  RSVPWithFeedback,
  CreateRSVPRequest,
  UpdateRSVPRequest,
  BulkRSVPResponse,
  BulkRSVPRequest,
  FeedbackItem,
  AdminRSVPOverrideRequest,
  AdminRSVPOverrideResponse,
} from '@/types/api';
import type { RSVPSizeCode } from '@/types/units';

/**
 * Options for fetching RSVPs
 */
export interface FetchRSVPsOptions {
  menuId?: number;
  memberId?: string;
  includeFeedback?: boolean;
  menuIds?: number[]; // Fetch feedback for these menu IDs (enables feedback without RSVP)
}

/**
 * Response type for fetchRSVPs with feedbackByMenuId
 */
export interface FetchRSVPsResult {
  rsvps: RSVPWithFeedback[];
  feedbackByMenuId?: Record<number, string>;
}

/**
 * Fetch RSVPs with optional filtering
 * Members see their own RSVPs automatically
 *
 * @param options - Filter options
 * @returns List of RSVPs and optional feedbackByMenuId map
 */
export async function fetchRSVPs(
  options: FetchRSVPsOptions = {}
): Promise<FetchRSVPsResult> {
  const params = new URLSearchParams();

  if (options.menuId !== undefined) {
    params.append('menuId', options.menuId.toString());
  }
  if (options.memberId) {
    params.append('memberId', options.memberId);
  }
  if (options.includeFeedback) {
    params.append('includeFeedback', 'true');
  }
  if (options.menuIds && options.menuIds.length > 0) {
    params.append('menuIds', options.menuIds.join(','));
  }

  const url = params.toString() ? `/api/rsvps?${params.toString()}` : '/api/rsvps';

  const response = await apiCall<RSVPsWithFeedbackResponse & { feedbackByMenuId?: Record<number, string> }>(url, {
    method: 'GET',
  });

  return {
    rsvps: response.rsvps,
    feedbackByMenuId: response.feedbackByMenuId,
  };
}

/**
 * Create a new RSVP
 * @param data - RSVP data (menuId and size)
 * @returns Created RSVP
 */
export async function createRSVP(data: CreateRSVPRequest): Promise<RSVP> {
  const response = await apiCall<RSVPResponse>('/api/rsvps', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.rsvp;
}

/**
 * Update an existing RSVP
 * @param id - RSVP ID
 * @param data - RSVP data to update (size and/or status)
 * @returns Updated RSVP
 */
export async function updateRSVP(id: number, data: UpdateRSVPRequest): Promise<RSVP> {
  const response = await apiCall<RSVPResponse>(`/api/rsvps/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.rsvp;
}

/**
 * Delete an RSVP
 * @param id - RSVP ID
 */
export async function deleteRSVP(id: number): Promise<void> {
  await apiCall<{ message: string }>(`/api/rsvps/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Bulk upsert RSVPs with feedback in a single transaction
 *
 * This uses the new /api/rsvps/bulk endpoint which handles:
 * - Creating new RSVPs
 * - Updating existing RSVPs
 * - Cancelling RSVPs not in selections
 * - Upserting feedback entries
 * - Deleting feedback when text is empty
 *
 * All operations are executed atomically in a single database transaction.
 *
 * @param selections - Array of menu selections
 * @param feedback - Array of feedback items (empty feedbackText deletes existing)
 * @param scopeMenuIds - Menu IDs currently visible/editable in the UI scope
 * @returns Results of all operations
 */
export async function bulkUpsertRSVPs(
  selections: Array<{ menuId: number; size: RSVPSizeCode; optedOutMenuRecipeIds?: number[] }>,
  feedback: FeedbackItem[] = [],
  scopeMenuIds: number[] = []
): Promise<BulkRSVPResponse> {
  const payload: BulkRSVPRequest = { selections, feedback };
  if (scopeMenuIds.length > 0) {
    payload.scopeMenuIds = scopeMenuIds;
  }

  const response = await apiCall<BulkRSVPResponse>('/api/rsvps/bulk', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response;
}

/**
 * Admin override for a single RSVP (member + menu)
 * @param memberId - Member ITS ID (string)
 * @param data - Override payload (menuId + size or SKIP)
 */
export async function adminOverrideRSVP(
  memberId: string,
  data: AdminRSVPOverrideRequest
): Promise<AdminRSVPOverrideResponse> {
  return await apiCall<AdminRSVPOverrideResponse>(`/api/admin/members/${memberId}/rsvps/override`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
